import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAppSettings } from '../contexts/SettingsContext';

// Sample blog post content - in production, this would come from an API or CMS
const BLOG_POSTS: Record<string, {
  title: string;
  excerpt: string;
  content: string;
  imageUrl: string;
  category: string;
  publishedAt: string;
  author: string;
  authorImage?: string;
  readingTime: string;
  tags: string[];
}> = {
  'como-elegir-alimento-perro': {
    title: 'Como Elegir el Mejor Alimento para tu Perro',
    excerpt: 'Guia completa para seleccionar la comida adecuada segun la edad, tamano y necesidades especiales de tu mascota.',
    content: `
## Introduccion

Elegir el alimento adecuado para tu perro es una de las decisiones mas importantes que puedes tomar como dueno responsable. Una buena nutricion es fundamental para la salud, energia y longevidad de tu mascota.

## Factores a Considerar

### Edad de tu Perro

- **Cachorros (0-12 meses):** Necesitan alimentos ricos en proteinas y calorias para su crecimiento.
- **Adultos (1-7 anos):** Requieren una dieta balanceada para mantener su peso y energia.
- **Senior (+7 anos):** Necesitan menos calorias pero mas nutrientes especificos para las articulaciones.

### Tamano y Raza

Los perros de razas grandes tienen necesidades diferentes a los de razas pequenas:

- **Razas pequenas:** Metabolismo mas rapido, necesitan alimentos con mayor densidad calorica.
- **Razas grandes:** Necesitan formulas especiales para prevenir problemas articulares.

## Como Leer las Etiquetas

Al revisar la etiqueta de un alimento para perros, busca:

1. **Proteina de origen animal** como primer ingrediente
2. **Sin subproductos** de baja calidad
3. **Grasas saludables** como omega-3 y omega-6
4. **Sin colorantes artificiales** ni conservantes daninos

## Nuestras Recomendaciones

En PetMas contamos con una amplia seleccion de alimentos premium para todas las etapas de vida de tu perro. Consulta con nuestros expertos para encontrar la mejor opcion para tu mascota.

## Conclusion

Invertir en una buena alimentacion es invertir en la salud de tu mejor amigo. No dudes en consultar con tu veterinario si tienes dudas especificas sobre la dieta de tu perro.
    `,
    imageUrl: '/blog/dog-food.jpg',
    category: 'Nutricion',
    publishedAt: '2026-01-15',
    author: 'Dr. Maria Garcia',
    authorImage: '/authors/maria-garcia.jpg',
    readingTime: '5 min',
    tags: ['alimentacion', 'nutricion', 'perros', 'salud'],
  },
  // Add more posts as needed
};

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { settings } = useAppSettings();

  const post = slug ? BLOG_POSTS[slug] : null;

  // Check if blog is enabled
  if (settings && !settings.features?.blogEnabled) {
    return (
      <div className="container py-12 text-center">
        <h1 className="text-2xl font-bold text-gray-900">Blog no disponible</h1>
        <p className="mt-2 text-gray-600">
          El blog no esta disponible en este momento.
        </p>
        <Link to="/" className="mt-4 btn btn-primary inline-block">
          Volver al inicio
        </Link>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="container py-12 text-center">
        <h1 className="text-2xl font-bold text-gray-900">Articulo no encontrado</h1>
        <p className="mt-2 text-gray-600">
          El articulo que buscas no existe o fue eliminado.
        </p>
        <Link to="/blog" className="mt-4 btn btn-primary inline-block">
          Ver todos los articulos
        </Link>
      </div>
    );
  }

  const formattedDate = new Date(post.publishedAt).toLocaleDateString('es-PE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // Build canonical URL
  const canonicalUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/blog/${slug}`
    : `/blog/${slug}`;

  // Article schema
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    image: post.imageUrl ? `${typeof window !== 'undefined' ? window.location.origin : ''}${post.imageUrl}` : undefined,
    author: {
      '@type': 'Person',
      name: post.author,
    },
    publisher: {
      '@type': 'Organization',
      name: settings?.seo?.siteName ?? 'PetMas',
      logo: {
        '@type': 'ImageObject',
        url: `${typeof window !== 'undefined' ? window.location.origin : ''}/logo.png`,
      },
    },
    datePublished: post.publishedAt,
    dateModified: post.publishedAt,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
  };

  return (
    <>
      <Helmet>
        <title>{post.title} | Blog PetMas</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        {post.imageUrl && <meta property="og:image" content={post.imageUrl} />}
        <meta property="article:published_time" content={post.publishedAt} />
        <meta property="article:author" content={post.author} />
        {post.tags.map((tag, i) => (
          <meta key={i} property="article:tag" content={tag} />
        ))}

        {/* Article Schema */}
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      </Helmet>

      <article className="bg-white">
        {/* Hero image */}
        {post.imageUrl && (
          <div className="w-full aspect-[3/1] bg-gray-100 relative overflow-hidden">
            <img
              src={post.imageUrl}
              alt={post.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          </div>
        )}

        <div className="container py-8 max-w-3xl">
          {/* Breadcrumb */}
          <nav className="mb-6 text-sm">
            <ol className="flex items-center gap-2 text-gray-500">
              <li>
                <Link to="/" className="hover:text-orange-500">Inicio</Link>
              </li>
              <li>/</li>
              <li>
                <Link to="/blog" className="hover:text-orange-500">Blog</Link>
              </li>
              <li>/</li>
              <li className="text-gray-900 font-medium truncate">{post.title}</li>
            </ol>
          </nav>

          {/* Category and date */}
          <div className="flex items-center gap-3 mb-4">
            <span className="px-3 py-1 text-xs font-medium bg-orange-100 text-orange-600 rounded-full">
              {post.category}
            </span>
            <time dateTime={post.publishedAt} className="text-sm text-gray-500">
              {formattedDate}
            </time>
            <span className="text-sm text-gray-500">· {post.readingTime} de lectura</span>
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            {post.title}
          </h1>

          {/* Author */}
          <div className="flex items-center gap-4 pb-8 border-b mb-8">
            <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
              {post.authorImage ? (
                <img src={post.authorImage} alt={post.author} className="w-full h-full object-cover" />
              ) : (
                <span className="text-xl font-medium text-gray-500">
                  {post.author.charAt(0)}
                </span>
              )}
            </div>
            <div>
              <p className="font-medium text-gray-900">{post.author}</p>
              <p className="text-sm text-gray-500">Experto en cuidado animal</p>
            </div>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none prose-orange">
            {/* Simple markdown-like rendering */}
            {post.content.split('\n').map((line, i) => {
              const trimmedLine = line.trim();

              if (trimmedLine.startsWith('## ')) {
                return <h2 key={i} className="text-2xl font-bold text-gray-900 mt-8 mb-4">{trimmedLine.replace('## ', '')}</h2>;
              }
              if (trimmedLine.startsWith('### ')) {
                return <h3 key={i} className="text-xl font-semibold text-gray-900 mt-6 mb-3">{trimmedLine.replace('### ', '')}</h3>;
              }
              if (trimmedLine.startsWith('- **')) {
                const match = trimmedLine.match(/- \*\*(.+?)\*\*:?\s*(.+)?/);
                if (match) {
                  return (
                    <p key={i} className="my-2 pl-4 border-l-2 border-orange-500">
                      <strong className="text-gray-900">{match[1]}</strong>
                      {match[2] && <span className="text-gray-600">: {match[2]}</span>}
                    </p>
                  );
                }
              }
              if (trimmedLine.startsWith('- ')) {
                return <li key={i} className="text-gray-600 ml-4 my-1">{trimmedLine.replace('- ', '')}</li>;
              }
              if (/^\d+\.\s/.test(trimmedLine)) {
                return <li key={i} className="text-gray-600 ml-4 my-1 list-decimal">{trimmedLine.replace(/^\d+\.\s/, '')}</li>;
              }
              if (trimmedLine === '') {
                return <br key={i} />;
              }
              return <p key={i} className="text-gray-600 my-3">{trimmedLine}</p>;
            })}
          </div>

          {/* Tags */}
          <div className="mt-8 pt-8 border-t">
            <p className="text-sm font-medium text-gray-700 mb-3">Etiquetas:</p>
            <div className="flex flex-wrap gap-2">
              {post.tags.map(tag => (
                <span
                  key={tag}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 cursor-pointer"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          {/* Share buttons */}
          <div className="mt-8 pt-8 border-t">
            <p className="text-sm font-medium text-gray-700 mb-3">Compartir:</p>
            <div className="flex gap-3">
              <ShareButton
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(canonicalUrl)}`}
                label="Facebook"
                bgColor="bg-blue-600"
              />
              <ShareButton
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(canonicalUrl)}&text=${encodeURIComponent(post.title)}`}
                label="Twitter"
                bgColor="bg-sky-500"
              />
              <ShareButton
                href={`https://api.whatsapp.com/send?text=${encodeURIComponent(`${post.title} ${canonicalUrl}`)}`}
                label="WhatsApp"
                bgColor="bg-green-500"
              />
            </div>
          </div>

          {/* Back to blog */}
          <div className="mt-12">
            <button
              onClick={() => navigate('/blog')}
              className="text-orange-500 hover:text-orange-600 font-medium flex items-center gap-2"
            >
              <span>←</span> Volver al blog
            </button>
          </div>
        </div>
      </article>
    </>
  );
}

function ShareButton({ href, label, bgColor }: { href: string; label: string; bgColor: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`${bgColor} text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity`}
    >
      {label}
    </a>
  );
}
